import { Location } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { ActivatedRoute, Router } from '@angular/router';
import { routePathName } from '@app/app.models';
import { ApiDocumentationService } from '@services/api-documentation';
import { DialogService } from '@services/dialog';
import { ProgressIndicatorService } from '@services/progress';
import { Subscription } from 'rxjs';
import {environment} from '@env/environment';

import { AuthService } from '@services/auth';
import { LocationService } from '@services/location/location.service';

@Component({
	selector: 'app-api-documentation',
	templateUrl: './api-documentation.component.html',
	styleUrls: ['./api-documentation.component.scss']
})
export class ApiDocumentationComponent implements OnInit, OnDestroy {
	@ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;
	@ViewChild('searchInput') searchInput: ElementRef;

	private apiDocumentationSub: Subscription;
	private apiDocumentationPath = '';

	private onUrlChangeSub: Subscription;
	private requesting = false;
	public loaded = false;
	public pageNotFound = false;
	public searchControl = new FormControl('');
	public searchResults = [];
	public searchQuery = '';
	public window = window;

	constructor(
		public apiDocumentationService: ApiDocumentationService,
		private route: ActivatedRoute,
		private router: Router,
		private location: Location,
		private locationService: LocationService,
		private progressIndicatorService: ProgressIndicatorService,
		private dialogService: DialogService,
		private authService: AuthService
	) {}

	public ngOnInit(): void {
		this.searchControl.setValue('');

		this.onUrlChange();

		this.searchControl.valueChanges.subscribe((query: string) => {
			if (typeof query === 'string') {
				this.searchQuery = query;
				this.searchResults = this.apiDocumentationService.search(query);
			}
		});
	}

	public ngOnDestroy(): void {
		this.onUrlChangeSub?.unsubscribe();
	}

	public searchSelected(event: MatAutocompleteSelectedEvent) {
		if (this.authService.isAuthenticated) {
			this.searchControl.setValue(this.searchQuery, { emitEvent: false });
			this.searchInput.nativeElement.blur();
			this.location.go(`/${routePathName.apiDocumentation}/${event.option.value.location}`);
			this.loadApiDocumentationUrl(`${event.option.value.location.split('#')[0]}index.html`);
		} else {
			this.authService.logout(`/${routePathName.apiDocumentation}/${event.option.value.location}`);
		}
	}

	/**
	 * Handle back click action for error message. Remove error message and depending on location we either:
	 * - If in base path, redirect to app root.
	 * 	We don't know if previous page is broken or not causing an endless loop.
	 * - If not on base path (i.e. a broken link within a documentation) we reload the selected documentation at root.
	 */
	public back() {
		this.pageNotFound = false;

		if (this.location.path() === `/${routePathName.apiDocumentation}`) {
			this.router.navigate(['/'], { relativeTo: this.route });
		} else {
			this.location.go(`/${routePathName.apiDocumentation}`);
			this.loadApiDocumentationUrl(this.getPagePath());
		}
	}

	/**
	 * Load file from S3 Path. On success, insert content and intercept link clicks
	 * in iFrame. On error, clear iFrame content and set boolean to display error message.
	 */
	public loadApiDocumentationUrl(url = 'index.html'): void {
		if (this.apiDocumentationPath !== `${url}`) {
			this.apiDocumentationSub?.unsubscribe();
			this.requesting = false;
		}

		if (!this.requesting) {
			this.apiDocumentationPath = `${url}`;
			this.loaded = false;
			this.progressIndicatorService.spinner(true);
			this.pageNotFound = false;
			this.requesting = true;

			this.apiDocumentationSub = this.apiDocumentationService.getApiDocumentation(url).subscribe(
				(res: any) => {
					this.apiDocumentationPath = '';
					this.loaded = true;

					// Push to next digest cycle to handle *ngIf statement for pageNotFound
					setTimeout(() => {
						this.interceptLinkClicks(res);
					});

					this.apiDocumentationService.getSearchIndex().subscribe();
					this.apiDocumentationService.getSearchIndex().subscribe();

					this.requesting = false;
				},
				() => {
					this.apiDocumentationPath = '';
					this.loaded = true;

					// Clear iFrame content, this to ensure content is cleared and load events triggered
					const iFrame = document.getElementById('api-docs');

					if (iFrame) {
						iFrame['contentWindow'].document.open();
						iFrame['contentWindow'].document.write('');
						iFrame['contentWindow'].document.close();
					}

					this.pageNotFound = true;
					this.progressIndicatorService.spinner(false);

					this.requesting = false;
				}
			);
		}
	}

	/**
	 * Watch for location URL changes (to ensure we capture non angular routing events) and
	 * load API content if state is available.
	 */
	private onUrlChange() {
		this.onUrlChangeSub = this.locationService.onUrlChange.subscribe((event) => {
			if (event.url.includes('?returnUrl=')) {
				return;
			}

			if (event.state && this.location.path().startsWith(`/${routePathName.apiDocumentation}`)) {
				this.loadApiDocumentationUrl(this.getPagePath());
			}
		});

		this.loadApiDocumentationUrl(this.getPagePath());
	}

	/**
	 * iFrame load event handler, on load ensure visibility is reset and if loaded (i.e. API is not currently
	 * retrieving content) hide the spinner.
	 */
	public iFrameLoaded() {
		const iFrame = document.getElementById('api-docs');

		if (iFrame) {
			iFrame.style.visibility = 'visible';

			const anchor = this.location.path(true).split('#');

			if (anchor.length > 1 && anchor[1].length > 0) {
				const el = iFrame['contentWindow'].document.querySelector(`#${anchor[1]}`);

				if (el) el.scrollIntoView(true);
			}
		}

		if (this.loaded) {
			this.progressIndicatorService.spinner(false);
		}
	}

	/**
	 * Loads content into iFrame and attaches event listener to all link clicks. On click,
	 * the links relative URL is transformed into a S3 document path, the location URL updated
	 * and a request for the S3 path initiated to load the required content.
	 */
	private interceptLinkClicks(content: string): void {
		const iFrame = document.getElementById('api-docs');

		if (!iFrame) return;

		iFrame.style.visibility = 'hidden';

		iFrame['contentWindow'].document.open();
		iFrame['contentWindow'].document.write(content);
		iFrame['contentWindow'].document.close();

		iFrame['contentWindow'].window.scrollTo(0, 0);

		iFrame['contentWindow'].onclick = () => {
			// Ensure autocomplete closes on any click within iFrame
			this.autocomplete?.closePanel();
		};

		const links = iFrame['contentWindow'].document.links;

		Array.from(links).forEach((link: HTMLElement) => {
			link.addEventListener('click', (e: Event) => {
				let href = e.currentTarget['getAttribute']('href');

				if (href.startsWith('#') || href.startsWith('/#')) {
					href = href.startsWith('/') ? href.substring(1) : href;

					const el = iFrame['contentWindow'].document.querySelector(href);

					if (el) el.scrollIntoView(true);

					e.preventDefault();

					this.location.go(`${this.location.path()}${href}`);

					return;
				}

				if (href.startsWith('http') || href.startsWith('www')) {
					window.open(href, '_blank');
					e.preventDefault();
					return;
				}

				iFrame.style.visibility = 'hidden';

				const url = new URL(href, document.baseURI + this.location.path().substring(1) + '/');

				href = `${url.origin}${url.pathname}`.replace(
					`${document.baseURI}api-documentation/`,
					''
				);

				if (href.endsWith('/') || href.length === 0) href += 'index.html';

				if (this.authService.isAuthenticated) {
					this.location.go(`/api-documentation/${href.replace('index.html', '')}${url.hash}`);
					this.loadApiDocumentationUrl(href);
					iFrame['contentWindow'].window.scrollTo(0, 0);
					e.preventDefault();
				} else {
					e.preventDefault();
					this.authService.logout(`/api-documentation/${href.replace('index.html', '')}${url.hash}`);
				}
			});
		});
	}

	/**
	 * Get file path out of normalized Location URL. Will remove base URL and ensure
	 * we return a string in the correct format for S3 with /index.html appended.
	 */
	private getPagePath(): string {
		let page = this.location.path().replace(`/${routePathName.apiDocumentation}`, '');

		if (page.length === 0) return 'index.html';

		if (page.startsWith('/')) page = page.substring(1);

		if (!page.endsWith('/index.html')) page += '/index.html';

		return page;
	}
}
